import { useState } from 'react';
import { ActionMenu, ActionList, TextInput } from '@primer/react';
import { PlusIcon, SearchIcon } from '@primer/octicons-react';
import styled from 'styled-components';
import { AddButton, ListToken } from './shared/tokens';
import { SectionContainer, ItemsContainer, ItemsList } from './shared/section-styles';
import { SectionTitleWithGuidance, SectionWarning } from './shared/section-guidance';
import { TargetIndustry } from '../../../../../../common/model/input/companies/products/ipr';
import { ATTRIBUTE_GUIDANCE } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';

interface TargetIndustriesProps {
  industries: TargetIndustryItem[];
  onIndustriesChange: (industries: TargetIndustryItem[]) => void;
  warning?: string;
}

interface TargetIndustryItem {
  id: TargetIndustry;
  title: string;
}

const TARGET_INDUSTRIES: TargetIndustryItem[] = Object.values(TargetIndustry).map(value => ({
  id: value,
  title: value,
}));

export function TargetIndustries({ industries, onIndustriesChange, warning }: TargetIndustriesProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const availableIndustries = TARGET_INDUSTRIES.filter(
    industry =>
      !industries.some(i => i.id === industry.id) && industry.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const addIndustry = (industry: TargetIndustryItem) => {
    onIndustriesChange([...industries, industry]);
    setIsMenuOpen(false);
    setSearchTerm('');
  };

  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  const removeIndustry = (industry: TargetIndustryItem) => {
    onIndustriesChange(industries.filter(ind => ind.id !== industry.id));
  };

  return (
    <SectionContainer>
      <SectionTitleWithGuidance title="Target industry" guidance={ATTRIBUTE_GUIDANCE.targetIndustry} />

      <ItemsContainer>
        <ItemsList>
          {industries.map(industry => (
            <ListToken
              key={industry.id}
              size="medium"
              onRemove={() => {
                removeIndustry(industry);
              }}
              text={industry.title}
            />
          ))}
          <ActionMenu open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
            <ActionMenu.Anchor>
              <AddButton size="medium" leadingVisual={PlusIcon} text="Add industry" />
            </ActionMenu.Anchor>

            <ActionMenu.Overlay width="medium">
              <SearchContainer>
                <TextInput
                  placeholder="Search industries..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                  leadingVisual={SearchIcon}
                  size="small"
                />
              </SearchContainer>
              <ScrollableActionListContainer>
                <ActionList>
                  <ActionList.Group title="Select an industry...">
                    {availableIndustries.length > 0 ? (
                      availableIndustries.map(industry => (
                        <ActionList.Item
                          key={industry.id}
                          onSelect={() => {
                            addIndustry(industry);
                          }}
                        >
                          {industry.title}
                        </ActionList.Item>
                      ))
                    ) : (
                      <ActionList.Item disabled>
                        {searchTerm ? 'No matching industries found' : 'All industries added'}
                      </ActionList.Item>
                    )}
                  </ActionList.Group>
                </ActionList>
              </ScrollableActionListContainer>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ItemsList>
      </ItemsContainer>
      <SectionWarning warning={warning} />
    </SectionContainer>
  );
}

const SearchContainer = styled.div`
  padding: var(--space__sm);
  border-bottom: 1px solid var(--borderColor-default);
`;

const ScrollableActionListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
